import { microsoftAuthService } from "./config";
import { authManagerInstance, AuthProvider } from "../values";
import { InteractionRequiredAuthError } from "@azure/msal-browser";

export const fetchCalendarEvents = async (account, setLoading, setEvents, setError) => {
    try {
        if (!account) {
            throw new Error("No account provided. Please sign in first.");
        }
        setLoading && setLoading(true);
        let accountId;
        if (typeof account === 'string') {
            accountId = account;
        } else if (account.id) {
            accountId = account.id;
        } else if (account.userData && account.userData.id) {
            accountId = account.userData.id;
        } else {
            const accounts = authManagerInstance.getAccounts(AuthProvider.MICROSOFT);
            const matchingAccount = accounts.find(acc =>
                acc.userData && acc.userData.username === (account.username || account.email)
            );
            if (matchingAccount) {
                accountId = matchingAccount.id;
            } else {
                throw new Error("Could not determine account ID");
            }
        }
        const getAccessToken = async (interactive = false, retryCount = 0) => {
            try {
                return await microsoftAuthService.getAccessToken(
                    accountId,
                    ['Calendars.Read', 'Calendars.ReadWrite', 'User.Read'],
                    interactive
                );
            } catch (error) {
                if (retryCount < 2 && (
                    error instanceof InteractionRequiredAuthError ||
                    error.message.includes("token") ||
                    error.message.includes("auth") ||
                    error.message.includes("session")
                )) {
                    return getAccessToken(true, retryCount + 1);
                }
                throw error;
            }
        };
        const fetchWithRetry = async (url, options, maxRetries = 3) => {
            let lastError;
            for (let attempt = 0; attempt < maxRetries; attempt++) {
                try {
                    const response = await fetch(url, options);
                    if (!response.ok) {
                        const errorData = await response.json().catch(() => ({}));
                        const errorMessage = errorData.error ?
                            errorData.error.message :
                            `Failed with status: ${response.status}`;
                        if (response.status === 401 || response.status === 403) {
                            options.headers.Authorization = `Bearer ${await getAccessToken(true)}`;
                            continue;
                        }

                        throw new Error(errorMessage);
                    }

                    return response;
                } catch (error) {
                    lastError = error;
                    if (error.message.includes("token") ||
                        error.message.includes("auth") ||
                        error.message.includes("Failed with status: 401")) {
                        try {
                            options.headers.Authorization = `Bearer ${await getAccessToken(true)}`;
                        } catch (tokenError) {
                            throw tokenError;
                        }
                    }
                    if (attempt < maxRetries - 1) {
                        await new Promise(r => setTimeout(r, 1000 * Math.pow(2, attempt)));
                    }
                }
            }
            throw lastError;
        };
        try {
            const accessToken = await getAccessToken();
            const response = await fetchWithRetry(
                'https://graph.microsoft.com/v1.0/me/calendar/events?$top=50&$select=subject,start,end,organizer,isOnlineMeeting',
                {
                    headers: {
                        'Authorization': `Bearer ${accessToken}`,
                        'Content-Type': 'application/json',
                        'Prefer': `outlook.timezone="${Intl.DateTimeFormat().resolvedOptions().timeZone}"`,
                    }
                }
            );
            const data = await response.json();
            if (!data.value) {
                throw new Error("Invalid response format from Microsoft Graph API");
            }
            const transformedEvents = data.value.map(event => {
                const startDateTime = new Date(event.start.dateTime);
                const endDateTime = new Date(event.end.dateTime);
                const options = { hour: '2-digit', minute: '2-digit', hour12: true };
                const startTime = startDateTime.toLocaleTimeString([], options);
                const endTime = endDateTime.toLocaleTimeString([], options);
                const startDate = startDateTime.toLocaleDateString([], {
                    year: 'numeric',
                    month: 'short',
                    day: 'numeric'
                });
                return {
                    id: event.id,
                    endTime: endTime,
                    startTime: startTime,
                    startDate: startDate,
                    subject: event.subject,
                    endDateTime: endDateTime,
                    startDateTime: startDateTime,
                    organizer: event.organizer?.emailAddress?.name || 'Unknown',
                    isOnlineMeeting: event.isOnlineMeeting || false,
                    account: account,
                    provider: AuthProvider.MICROSOFT,
                };
            });
            setEvents && setEvents(transformedEvents);
            return transformedEvents;
        } catch (error) {
            if (error.message.includes("token") ||
                error.message.includes("auth") ||
                error.message.includes("session")) {
                try {
                    await microsoftAuthService.login(['Calendars.Read', 'User.Read']);
                    const accessToken = await getAccessToken(true);
                    const response = await fetch(
                        'https://graph.microsoft.com/v1.0/me/calendar/events?$top=50&$select=subject,start,end,organizer,isOnlineMeeting',
                        {
                            headers: {
                                'Authorization': `Bearer ${accessToken}`,
                                'Content-Type': 'application/json',
                                'Prefer': `outlook.timezone="${Intl.DateTimeFormat().resolvedOptions().timeZone}"`,
                            }
                        }
                    );
                    if (!response.ok) {
                        throw new Error(`Failed to fetch events after re-login: ${response.status}`);
                    }
                    const data = await response.json();
                    const transformedEvents = data.value.map(event => {
                        const startDateTime = new Date(event.start.dateTime);
                        const endDateTime = new Date(event.end.dateTime);
                        const options = { hour: '2-digit', minute: '2-digit', hour12: true };
                        const startTime = startDateTime.toLocaleTimeString([], options);
                        const endTime = endDateTime.toLocaleTimeString([], options);
                        const startDate = startDateTime.toLocaleDateString([], {
                            year: 'numeric',
                            month: 'short',
                            day: 'numeric'
                        });
                        return {
                            id: event.id,
                            endTime: endTime,
                            startTime: startTime,
                            startDate: startDate,
                            subject: event.subject,
                            endDateTime: endDateTime,
                            startDateTime: startDateTime,
                            organizer: event.organizer?.emailAddress?.name || 'Unknown',
                            isOnlineMeeting: event.isOnlineMeeting || false,
                            account: account,
                            provider: AuthProvider.MICROSOFT,
                        };
                    });
                    setEvents && setEvents(transformedEvents);
                    return transformedEvents;
                } catch (loginError) {
                    throw new Error(`Authentication failed. Please try signing in again: ${loginError.message}`);
                }
            }
            throw error;
        }
    } catch (error) {
        setError && setError(`Failed to fetch calendar events: ${error.message}`);
        return [];
    } finally {
        setLoading && setLoading(false);
    }
};

export const createEvent = async (newEventData, account, events, setEvents, setError) => {
    try {
        if (!account) {
            throw new Error("No account provided. Please sign in first.");
        }
        let authority;
        if (typeof account === 'string') {
            const accountObj = authManagerInstance.getAccounts(AuthProvider.MICROSOFT)
                .find(acc => acc.id === account);
            if (accountObj?.userData?.tenantId) {
                authority = `https://login.microsoftonline.com/${accountObj.userData.tenantId}`;
            }
        } else if (account.tenantId) {
            authority = `https://login.microsoftonline.com/${account.tenantId}`;
        } else if (account.userData && account.userData.tenantId) {
            authority = `https://login.microsoftonline.com/${account.userData.tenantId}`;
        }
        if (!authority) {
            authority = 'https://login.microsoftonline.com/common';
        }
        const getAccessToken = async (interactive = false) => {
            try {
                if (typeof account === 'string') {
                    return await microsoftAuthService.getAccessToken(
                        account,
                        ['Calendars.ReadWrite'],
                        interactive,
                        authority
                    );
                } else if (account.id) {
                    return await microsoftAuthService.getAccessToken(
                        account.id,
                        ['Calendars.ReadWrite'],
                        interactive,
                        authority
                    );
                } else {
                    const tokenResponse = await (interactive
                        ? microsoftAuthService.msalInstance.acquireTokenPopup({
                            scopes: ['Calendars.ReadWrite'],
                            account: account,
                            authority: authority
                        })
                        : microsoftAuthService.msalInstance.acquireTokenSilent({
                            scopes: ['Calendars.ReadWrite'],
                            account: account,
                            authority: authority
                        })
                    );
                    return tokenResponse.accessToken;
                }
            } catch (error) {
                if (error instanceof InteractionRequiredAuthError && !interactive) {
                    return await getAccessToken(true);
                }
                throw error;
            }
        };

        try {
            const accessToken = await getAccessToken();
            const response = await fetch('https://graph.microsoft.com/v1.0/me/events', {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${accessToken}`,
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(newEventData)
            });

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                throw new Error(errorData.error ?
                    errorData.error.message :
                    `Failed to create event: ${response.status}`
                );
            }

            const createdEvent = await response.json();
            const formattedEvent = {
                ...createdEvent,
                account: account,
                provider: AuthProvider.MICROSOFT,
                startDateTime: new Date(createdEvent.start.dateTime),
                endDateTime: new Date(createdEvent.end.dateTime),
                attendees: createdEvent.attendees || [],
                startTime: new Date(createdEvent.start.dateTime).toLocaleTimeString([], {
                    hour: '2-digit',
                    minute: '2-digit',
                    hour12: true,
                }),
                endTime: new Date(createdEvent.end.dateTime).toLocaleTimeString([], {
                    hour: '2-digit',
                    minute: '2-digit',
                    hour12: true,
                }),
                startDate: new Date(createdEvent.start.dateTime).toLocaleDateString([], {
                    year: 'numeric',
                    month: 'short',
                    day: 'numeric',
                }),
                endDate: new Date(createdEvent.end.dateTime).toLocaleDateString([], {
                    year: 'numeric',
                    month: 'short',
                    day: 'numeric',
                }),
            };

            setEvents && setEvents([...events, formattedEvent]);
            return formattedEvent;
        } catch (error) {
            if (error.message && (error.message.includes("token") ||
                error.message.includes("auth") ||
                error.message.includes("401"))) {
                const accessToken = await getAccessToken(true);
                const response = await fetch('https://graph.microsoft.com/v1.0/me/events', {
                    method: 'POST',
                    headers: {
                        'Authorization': `Bearer ${accessToken}`,
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify(newEventData)
                });

                if (!response.ok) {
                    const errorData = await response.json().catch(() => ({}));
                    throw new Error(errorData.error ?
                        errorData.error.message :
                        `Failed to create event: ${response.status}`
                    );
                }

                const createdEvent = await response.json();
                const formattedEvent = {
                    ...createdEvent,
                    account: account,
                    provider: AuthProvider.MICROSOFT,
                    startDateTime: new Date(createdEvent.start.dateTime),
                    endDateTime: new Date(createdEvent.end.dateTime),
                    startTime: new Date(createdEvent.start.dateTime).toLocaleTimeString([], {
                        hour: '2-digit',
                        minute: '2-digit',
                        hour12: true
                    }),
                    endTime: new Date(createdEvent.end.dateTime).toLocaleTimeString([], {
                        hour: '2-digit',
                        minute: '2-digit',
                        hour12: true
                    }),
                    startDate: new Date(createdEvent.start.dateTime).toLocaleDateString([], {
                        year: 'numeric',
                        month: 'short',
                        day: 'numeric'
                    })
                };

                setEvents && setEvents([...events, formattedEvent]);
                return formattedEvent;
            } else {
                throw error;
            }
        }
    } catch (error) {
        setError && setError(`Failed to create event: ${error.message}`);
        throw error;
    }
};

export const updateEvent = async (eventId, updatedEventData, account, setEvents, setError, events) => {
    try {
        if (!account) {
            throw new Error("No account provided. Please sign in first.");
        }

        if (!eventId) {
            throw new Error("Event ID is required for updates");
        }

        let authority;
        if (typeof account === 'string') {
            const accountObj = authManagerInstance.getAccounts(AuthProvider.MICROSOFT)
                .find(acc => acc.id === account);

            if (accountObj?.userData?.tenantId) {
                authority = `https://login.microsoftonline.com/${accountObj.userData.tenantId}`;
            }
        } else if (account.tenantId) {
            authority = `https://login.microsoftonline.com/${account.tenantId}`;
        } else if (account.userData && account.userData.tenantId) {
            authority = `https://login.microsoftonline.com/${account.userData.tenantId}`;
        }

        if (!authority) {
            authority = 'https://login.microsoftonline.com/common';
        }

        const getAccessToken = async (interactive = false) => {
            try {
                if (typeof account === 'string') {
                    return await microsoftAuthService.getAccessToken(
                        account,
                        ['Calendars.ReadWrite'],
                        interactive,
                        authority
                    );
                } else if (account.id) {
                    return await microsoftAuthService.getAccessToken(
                        account.id,
                        ['Calendars.ReadWrite'],
                        interactive,
                        authority
                    );
                } else {
                    const tokenResponse = await (interactive ?
                        microsoftAuthService.msalInstance.acquireTokenPopup({
                            scopes: ['Calendars.ReadWrite'],
                            account: account,
                            authority: authority
                        }) :
                        microsoftAuthService.msalInstance.acquireTokenSilent({
                            scopes: ['Calendars.ReadWrite'],
                            account: account,
                            authority: authority
                        })
                    );
                    return tokenResponse.accessToken;
                }
            } catch (error) {
                if (error instanceof InteractionRequiredAuthError && !interactive) {
                    return await getAccessToken(true);
                }
                throw error;
            }
        };

        try {
            const accessToken = await getAccessToken();

            const response = await fetch(`https://graph.microsoft.com/v1.0/me/events/${eventId}`, {
                method: 'PATCH',
                headers: {
                    'Authorization': `Bearer ${accessToken}`,
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(updatedEventData)
            });

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                throw new Error(errorData.error ?
                    errorData.error.message :
                    `Failed to update event: ${response.status}`
                );
            }

            let updatedEvent;
            if (response.status !== 204) {
                updatedEvent = await response.json();
            } else {
                const getResponse = await fetch(`https://graph.microsoft.com/v1.0/me/events/${eventId}`, {
                    headers: {
                        'Authorization': `Bearer ${accessToken}`,
                        'Content-Type': 'application/json',
                    }
                });

                if (!getResponse.ok) {
                    throw new Error(`Failed to retrieve updated event: ${getResponse.status}`);
                }

                updatedEvent = await getResponse.json();
            }

            const formattedEvent = {
                ...updatedEvent,
                account: account,
                provider: AuthProvider.MICROSOFT,
                startDateTime: new Date(updatedEvent.start.dateTime),
                endDateTime: new Date(updatedEvent.end.dateTime),
                startTime: new Date(updatedEvent.start.dateTime).toLocaleTimeString([], {
                    hour: '2-digit',
                    minute: '2-digit',
                    hour12: true
                }),
                endTime: new Date(updatedEvent.end.dateTime).toLocaleTimeString([], {
                    hour: '2-digit',
                    minute: '2-digit',
                    hour12: true
                }),
                startDate: new Date(updatedEvent.start.dateTime).toLocaleDateString([], {
                    year: 'numeric',
                    month: 'short',
                    day: 'numeric'
                })
            };

            if (setEvents && events) {
                const updatedEvents = events.map(event =>
                    event.id === eventId ? formattedEvent : event
                );
                setEvents(updatedEvents);
            }

            return formattedEvent;
        } catch (error) {
            if (error.message && (error.message.includes("token") ||
                error.message.includes("auth") ||
                error.message.includes("401"))) {

                const accessToken = await getAccessToken(true);
                const response = await fetch(`https://graph.microsoft.com/v1.0/me/events/${eventId}`, {
                    method: 'PATCH',
                    headers: {
                        'Authorization': `Bearer ${accessToken}`,
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify(updatedEventData)
                });

                if (!response.ok) {
                    const errorData = await response.json().catch(() => ({}));
                    throw new Error(errorData.error ?
                        errorData.error.message :
                        `Failed to update event: ${response.status}`
                    );
                }
                let updatedEvent;
                if (response.status !== 204) {
                    updatedEvent = await response.json();
                } else {
                    const getResponse = await fetch(`https://graph.microsoft.com/v1.0/me/events/${eventId}`, {
                        headers: {
                            'Authorization': `Bearer ${accessToken}`,
                            'Content-Type': 'application/json',
                        }
                    });

                    if (!getResponse.ok) {
                        throw new Error(`Failed to retrieve updated event: ${getResponse.status}`);
                    }

                    updatedEvent = await getResponse.json();
                }
                const formattedEvent = {
                    ...updatedEvent,
                    account: account,
                    provider: AuthProvider.MICROSOFT,
                    startDateTime: new Date(updatedEvent.start.dateTime),
                    endDateTime: new Date(updatedEvent.end.dateTime),
                    startTime: new Date(updatedEvent.start.dateTime).toLocaleTimeString([], {
                        hour: '2-digit',
                        minute: '2-digit',
                        hour12: true
                    }),
                    endTime: new Date(updatedEvent.end.dateTime).toLocaleTimeString([], {
                        hour: '2-digit',
                        minute: '2-digit',
                        hour12: true
                    }),
                    startDate: new Date(updatedEvent.start.dateTime).toLocaleDateString([], {
                        year: 'numeric',
                        month: 'short',
                        day: 'numeric'
                    })
                };

                if (setEvents && events) {
                    const updatedEvents = events.map(event =>
                        event.id === eventId ? formattedEvent : event
                    );
                    setEvents(updatedEvents);
                }
                return formattedEvent;
            } else {
                throw error;
            }
        }
    } catch (error) {
        setError && setError(`Failed to update event: ${error.message}`);
        throw error;
    }
};

export const deleteEvent = async (eventId, account, setEvents, setError, events) => {
    try {
        if (!account) {
            throw new Error("No account provided. Please sign in first.");
        }

        if (!eventId) {
            throw new Error("Event ID is required for deletion");
        }
        let authority;
        if (typeof account === 'string') {
            const accountObj = authManagerInstance.getAccounts(AuthProvider.MICROSOFT)
                .find(acc => acc.id === account);

            if (accountObj?.userData?.tenantId) {
                authority = `https://login.microsoftonline.com/${accountObj.userData.tenantId}`;
            }
        } else if (account.tenantId) {
            authority = `https://login.microsoftonline.com/${account.tenantId}`;
        } else if (account.userData && account.userData.tenantId) {
            authority = `https://login.microsoftonline.com/${account.userData.tenantId}`;
        }

        if (!authority) {
            authority = 'https://login.microsoftonline.com/common';
        }
        const getAccessToken = async (interactive = false) => {
            try {
                if (typeof account === 'string') {
                    return await microsoftAuthService.getAccessToken(
                        account,
                        ['Calendars.ReadWrite'],
                        interactive,
                        authority
                    );
                } else if (account.id) {
                    return await microsoftAuthService.getAccessToken(
                        account.id,
                        ['Calendars.ReadWrite'],
                        interactive,
                        authority
                    );
                } else {
                    const tokenResponse = await (interactive ?
                        microsoftAuthService.msalInstance.acquireTokenPopup({
                            scopes: ['Calendars.ReadWrite'],
                            account: account,
                            authority: authority
                        }) :
                        microsoftAuthService.msalInstance.acquireTokenSilent({
                            scopes: ['Calendars.ReadWrite'],
                            account: account,
                            authority: authority
                        })
                    );
                    return tokenResponse.accessToken;
                }
            } catch (error) {
                if (error instanceof InteractionRequiredAuthError && !interactive) {
                    return await getAccessToken(true);
                }
                throw error;
            }
        };

        try {
            const accessToken = await getAccessToken();
            const response = await fetch(`https://graph.microsoft.com/v1.0/me/events/${eventId}`, {
                method: 'DELETE',
                headers: {
                    'Authorization': `Bearer ${accessToken}`,
                    'Content-Type': 'application/json',
                }
            });

            if (!response.ok) {
                const errorText = await response.text();
                let errorMessage = `Failed to delete event: ${response.status}`;
                try {
                    const errorData = JSON.parse(errorText);
                    errorMessage = errorData.error ? errorData.error.message : errorMessage;
                }
                catch (e) { }
                throw new Error(errorMessage);
            }

            if (setEvents && events) {
                const updatedEvents = events.filter(event => event.id !== eventId);
                setEvents(updatedEvents);
            }

            return true;
        } catch (error) {
            if (error.message && (error.message.includes("token") ||
                error.message.includes("auth") ||
                error.message.includes("401"))) {

                const accessToken = await getAccessToken(true);
                const response = await fetch(`https://graph.microsoft.com/v1.0/me/events/${eventId}`, {
                    method: 'DELETE',
                    headers: {
                        'Authorization': `Bearer ${accessToken}`,
                        'Content-Type': 'application/json',
                    }
                });

                if (!response.ok) {
                    const errorText = await response.text();
                    let errorMessage = `Failed to delete event: ${response.status}`;
                    try {
                        const errorData = JSON.parse(errorText);
                        errorMessage = errorData.error ? errorData.error.message : errorMessage;
                    }
                    catch (e) { }
                    throw new Error(errorMessage);
                }

                if (setEvents && events) {
                    const updatedEvents = events.filter(event => event.id !== eventId);
                    setEvents(updatedEvents);
                }
                return true;
            } else {
                throw error;
            }
        }
    } catch (error) {
        setError && setError(`Failed to delete event: ${error.message}`);
        throw error;
    }
};